/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { noop } from "lodash";
import { Box } from "@towersystems/towersystems-design-system";
import { DropdownLinkV2 } from "components/dropdown-link-v2";

import moment from "moment";
import Dropdown from "react-dropdown";
import { LuCalendarCog } from "react-icons/lu";
import { Text } from "components/text";

/**
 * Internal dependencies
 */
import {
  getDateRange,
  getCompareDateRange,
  getEndDate,
  getStartDate,
  formatDateString,
  formatCompareString,
} from "state/ui/date-range/selectors";
import { setDateRange } from "state/ui/date-range/actions";
import dateParser from "lib/date-parser";
import { ButtonV2 } from "components/button-v2";

/**
 * Style dependencies
 */
import "./styles.scss";

export const options = [
  { label: "Today", value: "Today" },
  { label: "Yesterday", value: "Yesterday" },
  { label: "This week", value: "This week" },
  { label: "Last week", value: "Last week" },
  { label: "Last 30 days", value: "Last 30 days" },
  { label: "Last 60 days", value: "Last 60 days" },
  { label: "Last 180 days", value: "Last 180 days" },
  { label: "This year", value: "This year" },
];

const DateRangePicker = ({
  dateRange = noop,
  defaultStartDate,
  defaultEndDate,
  compareDateRange,
  formatedDateString,
  formatedCompareString,
  ...props
}) => {
  const [compareToType, setCompareToType] = useState("previous");
  const [dropdownDateRange, setDropdownDateRange] = useState(dateRange);

  const [dates, setDates] = useState({
    startDate: defaultStartDate && moment(defaultStartDate),
    endDate: moment(defaultEndDate),
  });

  useEffect(() => {
    try {
      let dates = dateParser(dateRange);
      setDates({ startDate: moment(dates[0]), endDate: moment(dates[1]) });
    } catch (error) {}
  }, []);

  const onDropdownRangeChange = ({ value }) => {
    try {
      let dates = dateParser(value);
      setDates({ startDate: moment(dates[0]), endDate: moment(dates[1]) });
    } catch (error) {}
    setDropdownDateRange(value);
  };

  const applyFilters = () => {
    let startDateString =
      dates.startDate && dates.startDate.format("YYYY-MM-DD");
    let endDateString = dates.endDate && dates.endDate.format("YYYY-MM-DD");

    props.setDateRange(
      dropdownDateRange,
      startDateString,
      endDateString || startDateString,
      compareToType,
    );
  };

  // format strings
  const startDateString =
    dates.startDate && dates.startDate.format("YYYY-MM-DD");

  const endDateString = dates.endDate && dates.endDate.format("YYYY-MM-DD");

  return (
    <Box className="date-range-picker" display="flex" alignItems="center">
      <Box className="date-range-picker__dropdown-wrapper">
        <Box>
          <DropdownLinkV2
            className="date-range-picker__dropdown"
            label={dateRange ?? formatedDateString}
            icon={<LuCalendarCog size="20" />}
            showArrow={false}
            activatorProps={{
              compact: true,
            }}
          >
            {({ close }) => {
              return (
                <Box>
                  <Box padding="large">
                    <div className="mb-2">
                      <Text variant="headingXxs">Date range</Text>
                    </div>
                    <Dropdown
                      value={dropdownDateRange}
                      options={options}
                      onChange={onDropdownRangeChange}
                      placeholder="Custom"
                    />
                  </Box>
                  <Box
                    marginBottom="large"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    padding="large"
                  >
                    <Box width="full" marginRight="medium">
                      <div className="mb-2">
                        <Text variant="headingXxs">Starting</Text>
                      </div>
                      <input
                        type="date"
                        name="start date"
                        value={startDateString}
                        onChange={(e) => {
                          setDropdownDateRange(null);
                          setDates({
                            ...dates,
                            startDate: moment(e.target.value),
                          });
                        }}
                      />
                    </Box>
                    <Box width="full" marginLeft="medium">
                      <div className="mb-2">
                        <Text variant="headingXxs">Ending</Text>
                      </div>
                      <input
                        type="date"
                        name="end date"
                        value={endDateString}
                        onChange={(e) => {
                          setDropdownDateRange(null);
                          setDates({
                            ...dates,
                            endDate: moment(e.target.value),
                          });
                        }}
                      />
                    </Box>
                  </Box>

                  <Box padding="large">
                    <div className="mb-2">
                      <Text variant="headingXxs">Compare to</Text>
                    </div>
                    <Dropdown
                      value={compareToType}
                      options={[
                        { label: "Previous Period", value: "previous" },
                        { label: "Last Year", value: "last_year" },
                      ]}
                      onChange={({ value }) => {
                        setCompareToType(value);
                      }}
                    />
                  </Box>
                  <div className="flex gap-3 p-4">
                    <ButtonV2
                      fullWidth
                      onClick={() => {
                        close();
                        applyFilters();
                      }}
                      filled
                      variant="primary"
                    >
                      Apply
                    </ButtonV2>
                    <ButtonV2 fullWidth onClick={() => close()}>
                      Cancel
                    </ButtonV2>
                  </div>
                </Box>
              );
            }}
          </DropdownLinkV2>
        </Box>
        <Box
          marginTop="small"
          className="absolute"
          style={{
            width: 200,
          }}
        >
          <Text size="xsmall" className="text-gray-400 font-semibold">
            Compare to: {formatedCompareString}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default connect(
  (state) => ({
    dateRange: getDateRange(state),
    compareDateRange: getCompareDateRange(state),
    defaultEndDate: getEndDate(state),
    defaultStartDate: getStartDate(state),
    formatedDateString: formatDateString(state),
    formatedCompareString: formatCompareString(state),
  }),
  {
    setDateRange,
  },
)(DateRangePicker);
